/**
 * AiSummaryBanner: AI 요약 알림 배너 (Jira DBPWHGUIUX-15293)
 * 마크업: http://172.16.114.131/html/rtc/rtc_new4_1.html (.rtcAiSummaryBanner)
 *
 * 회의 입장(joined) 시 1회 판정하여 우측 상단에 10초간 노출한다.
 * (회의 중 이전회의가 연결되어도 실시간으로 띄우지 않는다 — 재입장 시 판정)
 * 클릭 시 ONE AI 패널을 열고 프롬프트를 자동입력·전송한다. (utils/oneAi.ts)
 *
 * 배너 2종 — 방의 이전회의 연결 여부로 종류가 갈린다 (동시 노출 없음):
 * 이전회의가 연결된 방은 이전회의 배너만 판정하고, 참석 전 배너는 연결이 없는 방
 * 전용이다 (재입장 시에도 연결된 방에서는 참석 전 배너를 띄우지 않는다).
 *
 * 1) 참석 전 회의 요약 — 이전회의 연결이 없는 방 (AND 조건)
 *    - 회의 시작 후 5분 이상 경과
 *    - 실제 발화 시간 3분 이상 / 서로 다른 발화자 2명 이상 / STT 텍스트 1,000자 이상
 *    → 클릭 시 "제가 참석하기 전에 나눈 대화를 요약해주세요."
 *
 * 2) 이전회의 요약 — 이전회의(linkedMeeting)가 연결된 방
 *    - 이전회의에 STT 존재
 *    - 공유 정책(ALL) 또는 이전회의 실제 참석자(PREVIOUS_ONLY)
 *    → 클릭 시 "연결된 이전 회의에서 논의된 핵심 내용을 정리해줘."
 *      ('이전에 논의했던…'은 현재 회의 대화 조회(Filter)와 라우팅 경합에서 밀려 교체)
 *
 * 공통 전제: 회원 + ONE AI 배포자(isAbleOneAI). 같은 브라우저 세션에서
 * 방·배너종류당 1회만 노출.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { store } from '@/store';
import apiManager from '@/api';
import { getSyncedNow } from '@/utils/timeOffset';
import { openOneAiWithPrompt } from '@/utils/oneAi';
import { translateText } from '@/i18n';

type BannerType = 'PRE_JOIN_SUMMARY' | 'LINKED_MEETING_SUMMARY';

const BANNER_VISIBLE_MS = 10 * 1000;        // 미선택 시 10초 후 자동 제거
const MIN_ELAPSED_MS = 5 * 60 * 1000;       // 회의 시작 후 5분
const MIN_SPEECH_MS = 3 * 60 * 1000;        // 실제 발화 시간 3분
const MIN_SPEAKERS = 2;                     // 서로 다른 발화자 2명
const MIN_TEXT_LENGTH = 1000;               // STT 텍스트 1,000자

// ONE AI 생성도구 등록 프롬프트와 글자 단위로 동일해야 라우팅 적중 (위키 3-49-1/3-49-2 참고)
const PROMPTS: Record<BannerType, string> = {
  PRE_JOIN_SUMMARY: '제가 참석하기 전에 나눈 대화를 요약해주세요.',
  LINKED_MEETING_SUMMARY: '연결된 이전 회의에서 논의된 핵심 내용을 정리해줘.',
};

const shownKey = (roomId: string, type: BannerType) => `aiSummaryBannerShown:${roomId}:${type}`;

const isDevBuild = import.meta.env.VITE_APP_TYPE === 'productDev';

const ICON_ASSET_HOST = isDevBuild ? 'http://172.16.114.131/html/rtc' : 'https://static.wehago.com';
const ICON_OVERRIDE_CSS = `.rtc_new3 .rtcAiSummaryBanner.rtcAiSummaryBanner::before{background-image:url(${ICON_ASSET_HOST}/imgs/rtc/ic_ai_star.svg)}`;

const AiSummaryBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const conferenceStatus = useAppSelector((s) => s.conference.status);
  const isAbleOneAI = useAppSelector((s) => s.translation.isAbleOneAI);

  const [bannerType, setBannerType] = useState<BannerType | null>(null);
  const evaluatedRef = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showBanner = useCallback((type: BannerType, roomId: string) => {
    try { sessionStorage.setItem(shownKey(roomId, type), String(Date.now())); } catch { /* ignore */ }
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setBannerType(type);
    hideTimerRef.current = setTimeout(() => setBannerType(null), BANNER_VISIBLE_MS);
  }, []);

  // ─── 입장 후 1회 판정 ─────────────────────────────────────────────────────
  useEffect(() => {
    // isAbleOneAI는 joined 이후 비동기로 세팅되므로 둘 다 준비된 시점에 1회만 평가
    if (conferenceStatus !== 'joined' || !isAbleOneAI || evaluatedRef.current) return;

    const local = store.getState().local;
    const roomId = local.roomId;
    if (!roomId || local.isGuest) return;

    evaluatedRef.current = true;
    let cancelled = false;

    (async () => {
      const linked = local.linkedMeeting;

      // 이전회의가 연결된 방: 이전회의 배너만 판정한다.
      // 참석 전 요약 배너는 연결 없는 방 전용 — 이전회의 조건이 미충족이어도 띄우지 않는다.
      if (linked?.roomId) {
        // 이전회의 요약 — 공유 정책 권한 + 이전회의 STT 존재
        try {
          if (sessionStorage.getItem(shownKey(roomId, 'LINKED_MEETING_SUMMARY'))) return;
        } catch { /* ignore */ }
        try {
          // 공유 정책: ALL이면 전원, PREVIOUS_ONLY(또는 미설정)면 이전회의 실제 참석자만
          // (MeetingMaterialTab의 이전회의 자료 공유 판정과 동일 기준)
          let allowed = linked.accessType === 'ALL';
          if (!allowed) {
            const record = await apiManager.getRecordDetail(linked.roomId);
            if (cancelled) return;
            const myPortalId = local.userId;
            const myUserNo = local.userNo ? Number(local.userNo) : null;
            allowed = record.attendees.some(
              (a) => (a.portalId && a.portalId === myPortalId) || (myUserNo != null && a.userNo === myUserNo),
            );
          }
          if (!allowed) return;

          const stats = await apiManager.getSttStats(linked.roomId);
          if (cancelled) return;
          if (stats.textLength > 0) {
            showBanner('LINKED_MEETING_SUMMARY', roomId);
          }
        } catch (err) {
          console.warn('[AiSummaryBanner] 이전회의 요약 조건 조회 실패:', err);
        }
        return;
      }

      // 이전회의 연결이 없는 방: 참석 전 회의 요약 판정 (입장 시점 1회)
      try {
        if (sessionStorage.getItem(shownKey(roomId, 'PRE_JOIN_SUMMARY'))) return;
      } catch { /* ignore */ }
      try {
        const startDatetime = local.startDatetime;
        if (startDatetime && getSyncedNow() - startDatetime >= MIN_ELAPSED_MS) {
          const stats = await apiManager.getSttStats(roomId);
          if (cancelled) return;
          if (
            stats.speechDurationMs >= MIN_SPEECH_MS &&
            stats.speakerCount >= MIN_SPEAKERS &&
            stats.textLength >= MIN_TEXT_LENGTH
          ) {
            showBanner('PRE_JOIN_SUMMARY', roomId);
          }
        }
      } catch (err) {
        console.warn('[AiSummaryBanner] 참석 전 요약 조건 조회 실패:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [conferenceStatus, isAbleOneAI, showBanner]);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  // ─── 클릭: ONE AI 패널 열고 프롬프트 자동입력·전송 ────────────────────────
  const handleClick = useCallback(() => {
    if (!bannerType) return;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    setBannerType(null);

    const roomId = store.getState().local.roomId;
    const opened = openOneAiWithPrompt(PROMPTS[bannerType], roomId);
    if (opened) {
      // ONE AI RNB와 사이드바 동시 노출 방지 (OneAiButton과 동일)
      dispatch(uiActions.setOpenSideBar(false));
    }
  }, [bannerType, dispatch]);

  if (!bannerType) return null;

  const userName = store.getState().local.userName;

  return (
    <>
      <style>{ICON_OVERRIDE_CSS}</style>
      <button
        type="button"
        className="rtcAiSummaryBanner rtcAiSummaryBanner--floating"
        onClick={handleClick}
      >
        <span className="rtcAiSummaryBanner__text">
          {bannerType === 'PRE_JOIN_SUMMARY' ? (
            <>
              {translateText('반가워요. {{name}}님!', { name: userName })}
              <br />
              {translateText('참석하시기전에 나눈대화들을 정리해보았어요.')}
            </>
          ) : (
            <>
              {translateText('오늘 회의를 시작하기 전,')}
              <br />
              {translateText('지난번 논의되었던 핵심내용을 가볍게 훑어보세요!')}
            </>
          )}
        </span>
      </button>
    </>
  );
};

/**
 * [임시 게이팅] 개발기 검증 중 — 개발환경(VITE_APP_TYPE=productDev) 빌드에서만 노출한다.
 * 운영 오픈 시 이 래퍼를 제거하고 AiSummaryBanner를 직접 export 하면 된다.
 */
const AiSummaryBannerDevOnly: React.FC = () => (isDevBuild ? <AiSummaryBanner /> : null);

export default AiSummaryBannerDevOnly;
